package org.example.project.ui.viewmodel

import androidx.compose.runtime.Composable
import androidx.compose.runtime.CompositionLocalProvider
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.remember
import androidx.lifecycle.ViewModelStore
import androidx.lifecycle.ViewModelStoreOwner
import androidx.lifecycle.viewmodel.compose.LocalViewModelStoreOwner
import org.example.project.client.model.ActivityResponse

/**
 * Creates and remembers a [ViewModelStoreOwner] whose [ViewModelStore] is cleared
 * when this composable leaves composition or when [key] changes.
 */
@Composable
fun rememberViewModelStoreOwner(key: Any? = Unit): ViewModelStoreOwner {
    val store = remember(key) { ViewModelStore() }
    DisposableEffect(store) {
        onDispose {
            store.clear()
        }
    }
    return remember(store) {
        object : ViewModelStoreOwner {
            override val viewModelStore: ViewModelStore = store
        }
    }
}

/**
 * Provides a [ViewModelStoreOwner] via [LocalViewModelStoreOwner].
 * If an explicit [owner] is provided, it is used.
 * Otherwise, if [LocalViewModelStoreOwner.current] is already present, it is reused.
 * If none is available, a new managed [ViewModelStoreOwner] is remembered and its store
 * cleared on disposal.
 */
@Composable
fun ProvideViewModelStoreOwner(
    owner: ViewModelStoreOwner? = null,
    content: @Composable () -> Unit
) {
    val resolvedOwner = owner ?: LocalViewModelStoreOwner.current ?: rememberViewModelStoreOwner()
    CompositionLocalProvider(LocalViewModelStoreOwner provides resolvedOwner) {
        content()
    }
}

/**
 * Derives a stable key for the activity form ViewModel based on whether
 * a draft is being created or an existing activity is being edited.
 */
fun activityFormViewModelKey(activityToEdit: ActivityResponse?): String {
    return if (activityToEdit != null) {
        "ActivityFormViewModel_edit_${activityToEdit.id}"
    } else {
        "ActivityFormViewModel_new"
    }
}
